import React from "react";

import {getStatusBadgeLabel} from "@components/RefMan/Reports/ReportsList/ObserverReportsList.lib";
import {CalendarIcon, CurrencyEuroIcon, PencilIcon, TruckIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, TrashIcon, ArchiveBoxArrowDownIcon, ExclamationCircleIcon, CheckCircleIcon} from "@heroicons/react/24/outline";
import {RefManListItemType} from "@components/RefMan/RefManList/RefManList.type";
import {dateFormatWithWeekday} from "@pages/Planner/Absences/Absences.lib";
import {GamesCosts, GamesSchema, Nominations} from "@pages/Games/Games.type";
import {LevelSchema} from "@services/types/level";
import {Member} from "@services/types/member";
import Alert from "@ui-components/Alert";
import {TODAY} from "@utils/constants";
import {IntlShape} from "react-intl";


const getGameCost = (intl: IntlShape, game: GamesSchema, keys: (keyof GamesCosts)[]) => {
  return intl.formatNumber(
    keys.reduce((acc, k) => acc + game.costs[0][k], 0),
    {maximumFractionDigits: 0, style: "currency", currency: "EUR"}
  )
}

const DocumentStatusButton: React.FC<{
  game: GamesSchema;
  onDownload?: (codGame: string) => void;
  onUpload?: (codGame: string, isReplacing?: boolean) => void;
  onDelete?: (codGame: string) => void;
  uploadingGame?: string | null;
  deletingGame?: string | null;
  canDelete?: boolean;
  canUpload?: boolean;
  canDownload?: boolean;
  onMarkAtOffice?: (codGame: string) => void;
  markingAtOfficeGame?: string | null;
  canMarkAtOffice?: boolean;
  onReportAnomaly?: (codGame: string) => void;
  reportingAnomalyGame?: string | null;
  canReportAnomaly?: boolean;
  onValidateDocument?: (codGame: string) => void;
  validatingDocumentGame?: string | null;
  canValidateDocument?: boolean;
}> = ({ game, onDownload, onUpload, onDelete, uploadingGame, deletingGame, canDelete, canUpload, canDownload, onMarkAtOffice, markingAtOfficeGame, canMarkAtOffice, onReportAnomaly, reportingAnomalyGame, canReportAnomaly, onValidateDocument, validatingDocumentGame, canValidateDocument }) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const isUploading = uploadingGame === game.cod_game;
  const isDeleting = deletingGame === game.cod_game;
  const isMarkingAtOffice = markingAtOfficeGame === game.cod_game;
  const isReportingAnomaly = reportingAnomalyGame === game.cod_game;
  const isValidatingDocument = validatingDocumentGame === game.cod_game;

  const handleDownload = async () => {
    if (onDownload) {
      setIsDownloading(true);
      try {
        onDownload(game.cod_game);
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const handleUpload = (isReplacing: boolean = false) => {
    if (onUpload) {
      onUpload(game.cod_game, isReplacing);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(game.cod_game);
    }
  };

  const handleMarkAtOffice = () => {
    if (onMarkAtOffice) {
      onMarkAtOffice(game.cod_game);
    }
  };

  const handleReportAnomaly = () => {
    if (onReportAnomaly) {
      onReportAnomaly(game.cod_game);
    }
  };

  const handleValidateDocument = () => {
    if (onValidateDocument) {
      onValidateDocument(game.cod_game);
    }
  };

  const gameDate = new Date(game.dat_game);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  gameDate.setHours(0, 0, 0, 0);
  const isPastGame = gameDate <= today;

  // Referto caricato
  if (game.flg_documents) {
    const uploadDate = game.tms_documents_update 
      ? new Date(game.tms_documents_update).toLocaleDateString('it-IT', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '';
    
    // Referto caricato con anomalia da validare
    if (game.des_anomaly) {
      return (
        <>
          <div className='flex flex-col items-center gap-1 bg-orange-50 border border-orange-300 rounded-lg px-3 py-2 min-w-[180px]'>
            <div className='flex items-center gap-2 w-full'>
              <span className='text-sm font-semibold text-orange-700 flex-1'>Da validare</span>
              <div className='flex gap-2 items-center'>
                {onDownload && game.tms_documents_update && canDownload && (
                  <button
                    onClick={handleDownload}
                    disabled={isDownloading || isUploading || isDeleting || isValidatingDocument}
                    className='p-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm'
                    title='Scarica referto'
                  >
                    {isDownloading ? (
                      <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    ) : (
                      <ArrowDownTrayIcon className='h-5 w-5' />
                    )}
                  </button>
                )}
                {onValidateDocument && canValidateDocument && (
                  <button
                    onClick={handleValidateDocument}
                    disabled={isDownloading || isUploading || isDeleting || isValidatingDocument}
                    className='p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm'
                    title='Valida referto'
                  >
                    {isValidatingDocument ? (
                      <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    ) : (
                      <CheckCircleIcon className='h-5 w-5' />
                    )}
                  </button>
                )}
              </div>
            </div>
            <span className='text-xs text-orange-700 w-full text-left font-medium'>
              Nuovo referto caricato
            </span>
          </div>
        </>
      );
    }
    
    // Referto caricato normalmente
    return (
      <>
        <div className='flex flex-col items-center bg-am-50 border border-am-200 rounded-lg px-3 py-2 min-w-[180px]'>
          <div className='flex items-center gap-2 w-full'>
            <span className='text-sm font-semibold text-am-700 flex-1'>Referto caricato</span>
          </div>
          {(game.num_documents_warnings === -1 && !game.tms_documents_update) ? (
            <span className='text-xs text-am-600 w-full text-left'>
              già presso gli uffici
            </span>
          ) : game.user_documents_update && (
            <span className='text-xs text-am-600 w-full text-left'>
              da {game.user_documents_update.full_name} il {uploadDate}
            </span>
          )}
          <div className='flex gap-2 mt-1 items-center w-full justify-between'>
            {onDownload && game.tms_documents_update && canDownload && (
              <button
                onClick={handleDownload}
                disabled={isDownloading || isUploading || isDeleting || isReportingAnomaly}
                className='p-1 bg-am-600 hover:bg-am-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm'
                title='Scarica referto'
              >
                {isDownloading ? (
                  <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                ) : (
                  <ArrowDownTrayIcon className='h-4 w-4' />
                )}
              </button>
            )}
            {onReportAnomaly && canReportAnomaly && game.tms_documents_update && (
              <button
                onClick={handleReportAnomaly}
                disabled={isDownloading || isUploading || isDeleting || isReportingAnomaly}
                className='p-1 hover:bg-red-100 rounded transition-colors disabled:opacity-50 text-red-600'
                title='Segnala anomalia'
              >
                {isReportingAnomaly ? (
                  <div className='h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin' />
                ) : (
                  <ExclamationCircleIcon className='h-4 w-4' />
                )}
              </button>
            )}
            {onUpload && canUpload && (
              <button
                onClick={() => handleUpload(true)}
                disabled={isDownloading || isUploading || isDeleting || isReportingAnomaly}
                className='p-1 hover:bg-am-100 rounded transition-colors disabled:opacity-50 text-am-600'
                title='Sostituisci referto'
              >
                {isUploading ? (
                  <div className='h-4 w-4 border-2 border-am-600 border-t-transparent rounded-full animate-spin' />
                ) : (
                  <ArrowUpTrayIcon className='h-4 w-4' />
                )}
              </button>
            )}
            {onDelete && canDelete && game.tms_documents_update && (
              <button
                onClick={handleDelete}
                disabled={isDownloading || isUploading || isDeleting || isReportingAnomaly}
                className='p-1 hover:bg-red-100 rounded transition-colors disabled:opacity-50 text-red-600'
                title='Elimina referto'
              >
                {isDeleting ? (
                  <div className='h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin' />
                ) : (
                  <TrashIcon className='h-4 w-4' />
                )}
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  // Anomalia segnalata (flg_documents=false e des_anomaly valorizzato)
  if (!game.flg_documents && game.des_anomaly) {
    return (
      <div className='flex flex-col items-center gap-1 bg-red-50 border border-red-300 rounded-lg px-3 py-2 min-w-[180px]'>
        <div className='flex items-center gap-2 w-full'>
          <span className='text-sm font-semibold text-red-700 flex-1'>Anomalia segnalata</span>
          <div className='flex gap-2 items-center'>
            {onUpload && canUpload && (
              <button
                onClick={() => handleUpload(false)}
                disabled={isUploading}
                className='p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm'
                title='Carica nuovo referto'
              >
                {isUploading ? (
                  <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                ) : (
                  <ArrowUpTrayIcon className='h-5 w-5' />
                )}
              </button>
            )}
            <ExclamationCircleIcon className='h-5 w-5 text-red-600' />
          </div>
        </div>
        <span className='text-xs text-red-700 w-full text-left font-medium'>
          {game.des_anomaly && game.des_anomaly.length > 24 ? game.des_anomaly.substring(0, 24) + '...' : game.des_anomaly}
        </span>
      </div>
    );
  }

  // Gara futura - nessun alert
  if (!isPastGame) {
    return null;
  }

  // Gara passata senza referto - in attesa
  if (game.num_documents_warnings === undefined || game.num_documents_warnings === null || game.num_documents_warnings === 0) {
    return (
      <div className='flex flex-col items-center gap-1 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 min-w-[180px]'>
        <div className='flex items-center gap-2 w-full'>
          <span className='text-sm font-medium text-yellow-700 flex-1'>In attesa</span>
          <div className='flex gap-2 items-center'>
            {onUpload && canUpload && (
              <button
                onClick={() => handleUpload(false)}
                disabled={isUploading || isMarkingAtOffice}
                className='p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm'
                title='Carica referto PDF'
              >
                {isUploading ? (
                  <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                ) : (
                  <ArrowUpTrayIcon className='h-5 w-5' />
                )}
              </button>
            )}
            {onMarkAtOffice && canMarkAtOffice && (
              <button
                onClick={handleMarkAtOffice}
                disabled={isMarkingAtOffice || isUploading}
                className='p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm'
                title='Marca come presente negli uffici'
              >
                {isMarkingAtOffice ? (
                  <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                ) : (
                  <ArchiveBoxArrowDownIcon className='h-5 w-5' />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Referto mancante con warning
  const warningText = game.num_documents_warnings === 1 
    ? '1 warning inviato' 
    : `${game.num_documents_warnings} warning inviati`;
  
  return (
    <div className='flex flex-col items-center gap-1 bg-red-50 border border-red-200 rounded-lg px-3 py-2 min-w-[180px]'>
      <div className='flex items-center gap-2 w-full'>
        <span className='text-sm font-semibold text-red-700 flex-1'>Referto mancante</span>
        <div className='flex gap-2 items-center'>
          {onUpload && canUpload && (
            <button
              onClick={() => handleUpload(false)}
              disabled={isUploading || isMarkingAtOffice}
              className='p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm'
              title='Carica referto PDF'
            >
              {isUploading ? (
                <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
              ) : (
                <ArrowUpTrayIcon className='h-5 w-5' />
              )}
            </button>
          )}
          {onMarkAtOffice && canMarkAtOffice && (
            <button
              onClick={handleMarkAtOffice}
              disabled={isMarkingAtOffice || isUploading}
              className='p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm'
              title='Marca come presente negli uffici'
            >
              {isMarkingAtOffice ? (
                <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
              ) : (
                <ArchiveBoxArrowDownIcon className='h-5 w-5' />
              )}
            </button>
          )}
        </div>
      </div>
      <span className='text-xs text-red-600 w-full text-left'>
        {warningText}
      </span>
    </div>
  );
};

const getDocumentStatus = (
  game: GamesSchema, 
  onDownload?: (codGame: string) => void,
  onUpload?: (codGame: string, isReplacing?: boolean) => void,
  onDelete?: (codGame: string) => void,
  uploadingGame?: string | null,
  deletingGame?: string | null,
  canDelete?: boolean,
  canUpload?: boolean,
  canDownload?: boolean,
  onMarkAtOffice?: (codGame: string) => void,
  markingAtOfficeGame?: string | null,
  canMarkAtOffice?: boolean,
  onReportAnomaly?: (codGame: string) => void,
  reportingAnomalyGame?: string | null,
  canReportAnomaly?: boolean,
  onValidateDocument?: (codGame: string) => void,
  validatingDocumentGame?: string | null,
  canValidateDocument?: boolean
) => {
  return <DocumentStatusButton game={game} onDownload={onDownload} onUpload={onUpload} onDelete={onDelete} uploadingGame={uploadingGame} deletingGame={deletingGame} canDelete={canDelete} canUpload={canUpload} canDownload={canDownload} onMarkAtOffice={onMarkAtOffice} markingAtOfficeGame={markingAtOfficeGame} canMarkAtOffice={canMarkAtOffice} onReportAnomaly={onReportAnomaly} reportingAnomalyGame={reportingAnomalyGame} canReportAnomaly={canReportAnomaly} onValidateDocument={onValidateDocument} validatingDocumentGame={validatingDocumentGame} canValidateDocument={canValidateDocument} />;
}

const groupCost = (intl: IntlShape, game: GamesSchema, label: string, fee_col: keyof GamesCosts, trip_col: keyof GamesCosts) =>
  game.costs[0][fee_col] ? <div className="flex flex-row items-center">
    <span className="mx-1.5 text-xl">&#8226;</span>
    <span className="font-medium mr-1">{label}:</span>
    <span className="font-light">{getGameCost(intl, game, [fee_col, trip_col])}</span>
    {game.costs[0][trip_col] ?
      <div className="flex flex-row items-center gap-1 text-xs text-gray-500 ml-3">
        <TruckIcon className="w-4 h-4"/>
        <span>{getGameCost(intl, game, [trip_col])}</span>
      </div>
      : null
    }
  </div> : null


export const getStatusBadgeClasses = (status: string): string => {
  switch (status) {
    case 'ACC':
      return 'bg-green-500/10 ring-green-500/20'
    case 'TRA':
      return 'bg-blue-400/10 ring-blue-400/30'
    case 'TEM':
      return 'bg-blue-400/10 ring-blue-400/30'
    case 'REV':
      return 'bg-purple-400/10 ring-purple-400/30'
    case 'RIF':
      return 'bg-red-400/10 ring-red-400/20'
    default:
      return 'bg-gray-400/10 ring-gray-400/20'
  }
}

const showNomination = (role: Nominations[], label: string, key: 'tag' | 'member' = 'tag', showStatus: boolean = false) => {
  const isObs = ['O', 'OS'].includes(label)
  if (role.length > 0) {
    const color = role[0].status === 'TEM' ? 'text-gray-500 text-opacity-50' : 'text-am-700'
    return (<div className={`font-bold flex flex-row gap-2 items-center ${color} ${isObs ? 'mt-2 italic' : ''}`}>
      <span className="text-xs font-normal italic">{label}</span>
      <span>{role[0].member[key]}</span>
      {showStatus ?
        // <span className="text-xs font-light border rounded-lg py-0.5 px-1">
        <span
          className={'text-xs font-light rounded-lg py-0.5 px-1 ring-1 ring-inset ' + getStatusBadgeClasses(role[0].status)}>
          {getStatusBadgeLabel(role[0].status)}
        </span>
        : null}
    </div>)
  } else {
    return null
  }
}

export const getNominationRoles = (game: GamesSchema, okStatus: string[]) => {
    const ref1 = game.nominations.filter(i => i.role === 'REF1' && okStatus.includes(i.status))
    const ref2 = game.nominations.filter(i => i.role === 'REF2' && okStatus.includes(i.status))
    const obsr = game.nominations.filter(i => i.role === 'OBSR' && okStatus.includes(i.status))
    const sp = game.nominations.filter(i => i.role === 'SP' && okStatus.includes(i.status))
    const crono = game.nominations.filter(i => i.role === 'CRONO' && okStatus.includes(i.status))
    const a24s = game.nominations.filter(i => i.role === 'A24S' && okStatus.includes(i.status))
    const udcs = game.nominations.filter(i => i.role === 'UDCS' && okStatus.includes(i.status))
    const obsu = game.nominations.filter(i => i.role === 'OBSU' && okStatus.includes(i.status))
  return {ref1, ref2, obsr, sp, crono, a24s, udcs, obsu}
}


export const getListItems = (
  intl: any,
  items: GamesSchema[],
  group: 'ref' | 'udc' | 'all',
  showTemp: boolean,
  showCosts: boolean,
  showDocuments: boolean,
  setManagedGame?: React.Dispatch<React.SetStateAction<GamesSchema | undefined>>,
  onDownloadDocument?: (codGame: string) => void,
  onUploadDocument?: (codGame: string, isReplacing?: boolean) => void,
  onDeleteDocument?: (codGame: string) => void,
  uploadingGame?: string | null,
  deletingGame?: string | null,
  currentUserId?: number,
  canEditAny?: boolean,
  selectedGames?: Set<string>,
  onToggleSelection?: (codGame: string) => void,
  onMarkAtOffice?: (codGame: string) => void,
  markingAtOfficeGame?: string | null,
  onReportAnomaly?: (codGame: string) => void,
  reportingAnomalyGame?: string | null,
  onValidateDocument?: (codGame: string) => void,
  validatingDocumentGame?: string | null
): RefManListItemType[] | [] => {


  const okStatus = showTemp ? ['ACC', 'TEM', 'TRA'] : ['ACC', 'TRA']
  const isObserverView: boolean = !!setManagedGame

  return items.map((game: GamesSchema, index): RefManListItemType => {

    const {ref1, ref2, obsr, sp, crono, a24s, udcs, obsu} = getNominationRoles(game, okStatus)

    const previousObservers = group === 'ref'
      ? game.nominations.filter(i => i.role === 'OBSR' && !okStatus.includes(i.status)).length
      : game.nominations.filter(i => i.role === 'OBSU' && !okStatus.includes(i.status)).length

    // Check if current user is nominated in this game
    const isUserNominated = currentUserId 
      ? game.nominations.some(nom => nom.member.id_user === currentUserId)
      : false;

    // Check if current user is the first referee (REF1)
    const isFirstReferee = currentUserId
      ? game.nominations.some(nom => nom.role === 'REF1' && nom.member.id_user === currentUserId)
      : false;

    // Check if current user uploaded this document
    const isDocumentUploader = !!currentUserId && game.id_user_documents_update === currentUserId;

    // Download: allowed if has permission OR is nominated
    const canDownload = canEditAny || isUserNominated;

    // Upload/Delete: 
    // - if has documents:upload permission: always allowed
    // - if nominated: only if is first referee OR uploaded the document
    const canDelete = canEditAny || (isUserNominated && (isFirstReferee || isDocumentUploader));
    const canUpload = canEditAny || isUserNominated;

    // Mark at office: only for office users with documents:upload permission and document not uploaded
    const canMarkAtOffice = !!(canEditAny && !game.flg_documents);

    // Report anomaly: only for office users with documents:upload permission and document uploaded without anomaly
    const canReportAnomaly = !!(canEditAny && game.flg_documents && !game.des_anomaly);

    // Validate document: only for office users with documents:upload permission, document uploaded and has anomaly
    const canValidateDocument = !!(canEditAny && game.flg_documents && game.des_anomaly);

    return ({
      rowId: `game-${index}`,
      rowElement: <>
        <dt className="font-bold capitalize text-gray-900 flex flex-row items-center gap-2 justify-between">
          <div className="flex flex-row gap-2 items-center">
            {selectedGames !== undefined && onToggleSelection && game.flg_documents && (
              <input
                type="checkbox"
                checked={selectedGames.has(game.cod_game)}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelection(game.cod_game);
                }}
                className="h-5 w-5 text-am-600 border-gray-300 rounded focus:ring-am-500 cursor-pointer"
              />
            )}
            <div><CalendarIcon className="h-5 sm:h-7 w-5 sm:w-7"/></div>
            <div className="flex flex-row sm:flex-col whitespace-nowrap items-center sm:items-start gap-2 sm:gap-0">
              <span>{intl.formatDate(game.dat_game, dateFormatWithWeekday)}</span>
              <span className="sm:text-sm font-normal">{game.time_game}</span>
            </div>
          </div>
        </dt>
        <dd
          className="border-t sm:border-0 w-full my-1 sm:my-0 py-2 sm:py-0 flex flex-col gap-1 justify-center sm:col-span-2">
          <div className="flex flex-row items-center gap-2 text-sm ">
            <span className="font-bold text-gray-700">
              # {game.game_number}
            </span>
            <span className="text-gray-900 border rounded-xl py-0.5 px-2 border-gray-300">
              {game.level.des_level_extended}
            </span>
          </div>
          <span className="text-xxs sm:text-xs truncate overflow-hidden w-full">
            {game.team_a.team_alias} <i>vs</i> {game.team_b.team_alias}
          </span>
          {game.city ?
            <span className="text-xxs sm:text-xs truncate overflow-hidden w-full">
              {game.place}, <b>{game.city.city} ({game.city.cod_province})</b>
              </span>
            : null}
        </dd>
        <dd className="flex flex-row w-full sm:col-span-2 items-center">
          {['ref', 'all'].includes(group) ?
            <>
              <div className="w-full flex flex-col gap-1 justify-center mb-1 sm:mb-0">
                <div className="flex flex-col text-sm">
                  {showNomination(ref1, '1°', group === 'all' ? 'tag' : 'member')}
                  {showNomination(ref2, '2°', group === 'all' ? 'tag' : 'member')}
                  {showTemp || game.dat_game <= TODAY
                    ? showNomination(obsr, 'O', group === 'all' ? 'tag' : 'member', isObserverView)
                    : null
                  }
                  {setManagedGame && previousObservers && obsr.length === 0 ?
                    <div className="w-48 mt-2">
                      <Alert type="warning"
                             title={previousObservers > 1 ? previousObservers + ' revoche/rifiuti' : '1 revoca/rifiuto'}
                             extraSlim/>
                    </div>
                    : null
                  }
                </div>
              </div>
            </>
            : null
          }
          {['udc', 'all'].includes(group) ?
            <>
              <div className="w-full flex flex-col gap-1 justify-center">
                <div className="flex flex-col text-sm">
                  {showNomination(sp, 'SP', group === 'all' ? 'tag' : 'member')}
                  {showNomination(crono, 'CR', group === 'all' ? 'tag' : 'member')}
                  {showNomination(a24s, '24', group === 'all' ? 'tag' : 'member')}
                  {showNomination(udcs, 'ST', group === 'all' ? 'tag' : 'member')}
                  {showTemp || game.dat_game <= TODAY
                    ? showNomination(obsu, 'OS', group === 'all' ? 'tag' : 'member', isObserverView)
                    : null
                  }
                </div>
              </div>
            </>
            : null
          }
          {setManagedGame ?
            <div className='min-w-0 w-7 h-7 flex flex-auto justify-center cursor-pointer'
                 onClick={() => {
                   setManagedGame(game)
                 }}
            >
              <PencilIcon title="Gestisci la designazione"/>
            </div>
            : null
          }
          {showDocuments && getDocumentStatus(game, onDownloadDocument, onUploadDocument, onDeleteDocument, uploadingGame, deletingGame, canDelete, canUpload, canDownload, onMarkAtOffice, markingAtOfficeGame, canMarkAtOffice, onReportAnomaly, reportingAnomalyGame, canReportAnomaly, onValidateDocument, validatingDocumentGame, canValidateDocument)}
        </dd>
        {showCosts && game.costs.length ?
          <dd
            className="w-full my-1 sm:my-0 py-1 flex flex-col sm:flex-row gap-1 sm:gap-10 sm:col-span-5 sm:items-center justify-center bg-gray-100 rounded-lg text-sm">
            <div className="flex flex-row gap-5 sm:gap-10 p-2 sm:p-0 items-center"><CurrencyEuroIcon
              className="h-6 w-6"/>
              <span className="font-bold">{getGameCost(
                intl,
                game,
                ['amt_fee_ref', 'amt_fee_udc', 'amt_fee_obs', 'amt_trip_ref', 'amt_trip_udc', 'amt_trip_obs']
              )}
              </span>
            </div>
            {groupCost(intl, game, 'Arbitri', 'amt_fee_ref', 'amt_trip_ref')}
            {groupCost(intl, game, 'UdC', 'amt_fee_udc', 'amt_trip_udc')}
            {groupCost(intl, game, 'Osservatori', 'amt_fee_obs', 'amt_trip_obs')}
          </dd>
          : null
        }
      </>
    })
  });
}


export const getAllowedObservers = (observers: Member[], level: string, group: 'ref' | 'udc') => {
  let res: { value: number; name: string }[] = []
  observers.forEach((i: Member) => {
    const jobs = i.members_jobs.filter(
      j => {
        const levelList = j.job.levels?.split(';') ?? []
        const allowedRoles = group === 'udc' ? ['IST', 'OBS', 'TUT']
          : ['B2', 'C1', 'B/F', 'D'].includes(level) ? ['IST', 'OBS'] : ['IST', 'TUT']
        return allowedRoles.includes(j.job.job_type)
          && j.job.cod_group === group.toUpperCase()
          && (!j.job.levels ||
            (level === 'B2' && levelList.includes('B2')) ||
            (['C1', 'B/F'].includes(level) && levelList.includes('C')) ||
            (level === 'D' && levelList.includes('D')) ||
            (['B/M', 'A2/F'].includes(level) && levelList.includes('B/M')) ||
            (levelList.includes('REG') && !['B/M', 'A2/F', 'B2', 'C1', 'B/F', 'D'].includes(level))
          )
      })
    if (jobs.length) {
      res.push({value: i.id_fip_code, name: i.member})
    }
  })
  return res
}


export const getMaxLevelOrder = (filterLevelByRole: string, levels: LevelSchema[], group: 'ref' | 'udc') => {
  switch (filterLevelByRole) {
    case 'all':
      return 1
    case 'c':
      return levels.filter(i => i.cod_level === 'C1')[0].level_order
    case 'd':
      return levels.filter(i => i.cod_level === 'D')[0].level_order
    case 'personal':
      return group === 'ref'
        ? levels.filter(i => i.cod_level === 'C1')[0].level_order
        : 1
    default:
      return group === 'ref'
        ? levels.filter(i => i.cod_level === 'PM')[0].level_order
        : levels.filter(i => i.cod_level === 'B2')[0].level_order

  }
}